// THROWAWAY POC (RevoGrid evaluation) — delete this file with the rest of
// src/demo/tag-config-demo/ to remove.
//
// Dropdown cell editor built with the free package's `Editor()` helper, which
// mounts a real React component (so real MUI components) as the cell editor.
//
// Two things to know about this seam:
//
//  1. `Editor()` mounts the component in its OWN React root (createRoot), so it
//     is NOT inside the app's <ThemeProvider>. React context does not cross
//     roots, which means MUI components here would fall back to the default
//     MUI theme — Roboto, blue primary — instead of ours. `setEditorTheme()`
//     below is the bridge: the page pushes the live theme into this module and
//     the editor re-provides it. Ugly but it is the only way in without a
//     provider prop on the grid.
//  2. RevoGrid does not know the option list. `save(value)` writes whatever it
//     is handed, and every *bulk* write path (fill handle, clipboard paste,
//     setDataAt) bypasses the editor entirely. Validation is the caller's job
//     — see OPTION_COLUMNS / sanitizeOptionValues in TagConfigDemoPage.tsx.
import { useRef, useState } from 'react';
import { Editor } from '@revolist/react-datagrid';
import type { EditorType } from '@revolist/react-datagrid';
import { Box, MenuItem, Select, ThemeProvider, createTheme } from '@mui/material';
import type { Theme } from '@mui/material';

/**
 * The live app theme, pushed in from the page. Module-level because the editor
 * renders in a detached React root and cannot read context — see note 1 above.
 */
let sharedTheme: Theme | null = null;
const fallbackTheme = createTheme();

export const setEditorTheme = (theme: Theme) => {
  sharedTheme = theme;
};

type SelectEditorProps = EditorType & { options: readonly string[] };

/**
 * Geometry copied from the reference form so the open editor sits in the cell
 * the way an RHFSelect sits in a MUI TableCell:
 * src/features/gateway-details/components/EditGatewayTableRow/EditGatewayTableRow.tsx
 * uses `size="small"` + `maxHeight: '37.11px'` + 14px text, inside a cell with
 * MUI's 16px gutter.
 */
export const FIELD_HEIGHT = 37;
/** RHFSelect's menu: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP */
const MENU_MAX_HEIGHT = 48 * 4.5 + 8;

const SelectCellEditor = ({ column, val, save, close, options }: SelectEditorProps) => {
  // `val` is the pending editor value (RevoGrid pre-seeds it when editing starts
  // from a keypress); `column.value` is what is currently in the model.
  const initial = (val ?? column?.value ?? '') as string;
  const [open, setOpen] = useState(true);
  // save() already closes the editor and moves focus; calling close() after it
  // would fight that, so remember whether we saved.
  const savedRef = useRef(false);

  return (
    <ThemeProvider theme={sharedTheme ?? fallbackTheme}>
      {/*
        revogr-edit covers the whole cell, so the field is centred inside it
        with the same 16px gutter the cells use — otherwise the outline would
        sit flush against the cell edges, which no MUI table field does.
      */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          px: 2,
          boxSizing: 'border-box',
        }}
      >
        <Select
          open={open}
          autoFocus
          fullWidth
          size="small"
          // default variant — outlined, same as RHFSelect in the reference form
          // a value outside the list would make MUI warn and render blank anyway,
          // so surface it as "nothing selected" instead
          value={options.includes(initial) ? initial : ''}
          onChange={(event) => {
            savedRef.current = true;
            save(event.target.value);
          }}
          onClose={() => {
            setOpen(false);
            if (!savedRef.current) {
              close();
            }
          }}
          sx={{
            height: FIELD_HEIGHT,
            fontSize: (theme) => theme.typography.body2.fontSize,
            backgroundColor: (theme) => theme.palette.background.default,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              minHeight: 'unset',
              py: 0,
            },
          }}
          MenuProps={{
            // mirrors RHFSelect's MenuProps; the dropdown shadow itself comes
            // from the app theme's MuiPopover override, which rides along with
            // the ThemeProvider above
            PaperProps: {
              style: {
                maxHeight: MENU_MAX_HEIGHT,
                backgroundColor: (sharedTheme ?? fallbackTheme).palette.background.default,
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: 'inherit' }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </ThemeProvider>
  );
};

/**
 * Builds a RevoGrid editor constructor bound to a fixed option list.
 * Call once per column at module scope — `columns` has to stay a stable
 * reference.
 */
export const createSelectEditor = (options: readonly string[]) =>
  Editor((props: EditorType) => <SelectCellEditor {...props} options={options} />);
